import React from 'react';

import { RequiredFlag } from '../../required-flag';
import { MultiUrlParams } from '../multi-object-select-input/multi-object-select-input.component';
import { MultiSelect } from '../multi-select';
import { Option } from '../option';
import { SelectOption, SingleOption, isSingleOption } from '../select-types';
import useMultiSelectInputV2 from './multi-select-input-v2.controller';

export interface MultiSelectInputV2Props<T> {
    id?: string;
    label?: React.ReactNode;
    helpMessage?: React.ReactNode;

    options: SelectOption<T>[];
    onSelect?: (option: SingleOption<T>[] | null) => void;
    value?: T[] | null;
    searchValueOverwrite?: string;

    onSearch?: (value: string) => void;
    onScrollToBottom?: () => void;

    required?: boolean;
    disabled?: boolean;

    isClearable?: boolean;
    isLoading?: boolean;

    className?: string;

    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    hideLabel?: boolean;
    useQueryParam?: MultiUrlParams<T>;
    disableSearch?: boolean;
}

const ID = 'multi-select-input-v2';

export const MultiSelectInputV2 = <T,>(props: MultiSelectInputV2Props<T>) => {
    const viewProps = useMultiSelectInputV2(props);
    return (
        <div
            id={ID}
            className={`form-group w-full ${props.className}`}>
            <div className='mb-0! flex w-full flex-col'>
                {!props.hideLabel && (
                    <div className='inline-flex items-center gap-1'>
                        <span className='text-foreground-default text-sm font-medium'>{props.label}</span>
                        <RequiredFlag required={props.required} />
                    </div>
                )}
                {props.helpMessage && (
                    <>
                        <small className='form-text text-brand-muted'>{props.helpMessage}</small>
                    </>
                )}
            </div>
            <MultiSelect
                {...props}
                disableSearch={props.disableSearch}
                id={props.id || ID}
                onSelect={viewProps.onSelect}
                value={viewProps.value}
                disabled={props.disabled}
                onChange={viewProps.setSearchValue}
                searchValue={viewProps.searchValue}
                isClearable={props.isClearable}
                onScrollToBottom={props.onScrollToBottom}
                onFocus={props.onFocus}
                onRemove={viewProps.onRemove}
                onBlur={props.onBlur}>
                {viewProps.searchResultOptions &&
                    viewProps.searchResultOptions.map((option) => {
                        if (isSingleOption(option)) {
                            return (
                                <Option
                                    key={option.value}
                                    {...option}
                                />
                            );
                        }
                        return (
                            <>
                                <div className='text-foreground-default bg-background-surface flex w-full justify-start ps-2 font-semibold uppercase'>
                                    {option.label}
                                </div>
                                {option.options.map((option: SingleOption<T>) => (
                                    <Option
                                        key={option.value}
                                        {...option}
                                    />
                                ))}
                            </>
                        );
                    })}
            </MultiSelect>
        </div>
    );
};
